<template>
    <button
        type="button"
        :class="active ? 'bg-board-card-hover ring-1 ring-primary' : 'bg-board-card ring-1 ring-panel-border hover:bg-board-card-hover'"
        class="flex w-full flex-col !rounded-card px-4 py-3.5 text-left transition"
        :data-testid="`board-item-${workOrder.id}`"
        @click="$emit('select', workOrder.id)"
    >
        <div class="flex items-center justify-between gap-2">
            <work-order-type-icon :text="workOrder.code" :work-order="workOrder" class="min-w-0" style="min-height: 24px" />
            <work-order-priority v-if="priorityEnabled" :priority="workOrder.priority" class="shrink-0" />
        </div>

        <h3 class="line-clamp-2 text-sm font-semibold text-body">{{ workOrder.title }}</h3>

        <div class="flex flex-col gap-1.5">
            <div v-if="locationLabel" class="truncate text-xs text-gray-text">{{ locationLabel }}</div>

            <div v-if="workOrder.asset?.serialNumber" class="truncate text-xs text-gray-text">
                {{ trans('assets.overview.cell.serial_number') }}: {{ workOrder.asset.serialNumber }}
            </div>

            <div class="text-xs" :class="{ 'text-danger': isOverdue, 'text-warning': isUpcoming, 'text-gray-text': !isOverdue && !isUpcoming }">
                {{ dateLabel }}
            </div>
        </div>
    </button>
</template>

<script setup>
import WorkOrderPriority from '@/Pages/WorkOrders/Modals/WorkOrderPriority.vue';
import WorkOrderTypeIcon from '@/Pages/WorkOrders/Modals/WorkOrderTypeIcon.vue';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import fullDate from '@/filters/fullDate.js';
import { trans } from '@/mixins/i18n.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { workOrderIsOverdue } from '@/Pages/WorkOrders/workOrderOverdue.ts';

defineEmits(['select']);

const props = defineProps({
    workOrder: {
        type: Object,
        required: true,
    },
    active: {
        type: Boolean,
        default: false,
    },
});

const page = usePage();
const currentLocale = computed(() => page.props.currentLocale || 'en');
const priorityEnabled = hasFeature('work_order_priority');

const isDone = computed(() => props.workOrder.status?.value === 'done');

// Airport (IATA) first, then descending to the most specific location. Externals can span multiple
// airports, so the airport identifier leads and survives truncation.
const locationLabel = computed(() =>
    [...(props.workOrder?.asset?.location?.hierarchy ?? [])]
        .sort((a, b) => a.level - b.level)
        .map((loc) => (loc.level === 0 ? (loc.iataCode ?? loc.name) : loc.name))
        .join(' / '),
);

// dueDate is a calendar date (date-only); compare whole days so the buckets don't shift
// with the viewer's timezone the way an hours-from-now diff would.
const daysUntilDue = computed(() => {
    if (!props.workOrder.dueDate) {
        return null;
    }

    return Math.round(DateTime.fromISO(props.workOrder.dueDate).diff(DateTime.now().startOf('day'), 'days').days);
});

// Backend-authoritative overdue flag, matching WorkOrderCard and the board.
const isOverdue = computed(() => !isDone.value && workOrderIsOverdue(props.workOrder));
// Due within the next two days and not yet overdue, so a card never gets both states.
const isUpcoming = computed(
    () => !isDone.value && !isOverdue.value && daysUntilDue.value !== null && daysUntilDue.value >= 0 && daysUntilDue.value <= 2,
);

const resolvedDateLabel = computed(() => {
    if (props.workOrder.performedAt) {
        return fullDate(props.workOrder.performedAt, currentLocale.value);
    }

    return props.workOrder.statusChangedAt ? fullDate(props.workOrder.statusChangedAt, currentLocale.value) : '';
});

const dateLabel = computed(() => {
    if (isDone.value) {
        const resolved = resolvedDateLabel.value;
        return resolved ? `${trans('workorder.card.label.resolved_date')}: ${resolved}` : trans('workorder.card.label.resolved_date');
    }

    const due = props.workOrder.dueDate ? fullDate(props.workOrder.dueDate, currentLocale.value) : '-';
    return `${trans('workorder.card.label.due_date')}: ${due}`;
});
</script>
